import { useEffect, useState, useCallback } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import { ClockCounterClockwise, User, Funnel, X } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import Pagination from "./Pagination";

function timeAgo(dateStr) {
  if (!dateStr) return "-";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const actionColors = {
  approved: "text-[#10B981]",
  verified: "text-[#10B981]",
  rejected: "text-[#EF4444]",
  created: "text-[#00E5FF]",
  saved: "text-[#F59E0B]",
  Seed: "text-[#A78BFA]",
  Bulk: "text-[#F59E0B]",
};

function getActionColor(action) {
  for (const [key, color] of Object.entries(actionColors)) {
    if (action.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return "text-[#A1A1AA]";
}

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterActions, setFilterActions] = useState([]);
  const [filterAdmins, setFilterAdmins] = useState([]);
  const [selAction, setSelAction] = useState("");
  const [selAdmin, setSelAdmin] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const pageSize = 12;
  const ws = useWebSocket();

  const fetchEntries = useCallback((p) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p || page, page_size: pageSize });
    if (selAction) params.append("action", selAction);
    if (selAdmin) params.append("admin_name", selAdmin);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    axios
      .get(`${API}/admin/audit-log?${params.toString()}`, { headers: getAuthHeaders() })
      .then((res) => {
        setEntries(res.data.items || []);
        setTotal(res.data.total || 0);
        setFilterActions(res.data.filters?.actions || []);
        setFilterAdmins(res.data.filters?.admins || []);
      })
      .catch((err) => logger.error("Audit log error:", err))
      .finally(() => setLoading(false));
  }, [page, selAction, selAdmin, startDate, endDate]);

  useEffect(() => { fetchEntries(page); }, [page, fetchEntries]);

  useEffect(() => {
    if (!ws) return;
    const unsub1 = ws.subscribe("withdrawal_update", () => fetchEntries(page));
    const unsub2 = ws.subscribe("kyc_update", () => fetchEntries(page));
    return () => { unsub1(); unsub2(); };
  }, [ws, fetchEntries, page]);

  const hasFilters = selAction || selAdmin || startDate || endDate;
  const clearFilters = () => {
    setSelAction(""); setSelAdmin(""); setStartDate(""); setEndDate(""); setPage(1);
  };

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="audit-log">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(0, 229, 255, 0.1)" }}>
            <ClockCounterClockwise size={20} weight="duotone" className="text-[#00E5FF]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Audit Log
            </h3>
            <p className="text-[10px] text-[#A1A1AA] mt-0.5">{total} {hasFilters ? "filtered" : "total"} entries</p>
          </div>
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            data-testid="audit-clear-filters"
            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors"
          >
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid="audit-filters">
        <Funnel size={14} className="text-[#A1A1AA]" />
        <select
          value={selAction}
          onChange={(e) => { setSelAction(e.target.value); setPage(1); }}
          data-testid="audit-filter-action"
          className="px-2 py-1 rounded text-xs bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#00E5FF]/40"
        >
          <option value="">All actions</option>
          {filterActions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          value={selAdmin}
          onChange={(e) => { setSelAdmin(e.target.value); setPage(1); }}
          data-testid="audit-filter-admin"
          className="px-2 py-1 rounded text-xs bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#00E5FF]/40"
        >
          <option value="">All admins</option>
          {filterAdmins.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          type="date"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
          data-testid="audit-filter-start"
          className="px-2 py-1 rounded text-xs bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#00E5FF]/40"
        />
        <span className="text-[10px] text-[#52525B]">to</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
          data-testid="audit-filter-end"
          className="px-2 py-1 rounded text-xs bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#00E5FF]/40"
        />
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-white/10 hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Action</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Admin</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Details</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-xs text-[#A1A1AA]" data-testid="audit-empty">
                      {hasFilters ? "No entries match your filters" : "No audit entries yet"}
                    </TableCell>
                  </TableRow>
                ) : entries.map((entry) => (
                  <TableRow key={entry.audit_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                    <TableCell>
                      <span className={`text-xs font-medium ${getActionColor(entry.action)}`}>
                        {entry.action}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <User size={12} className="text-[#A1A1AA]" />
                        <span className="text-xs text-white">{entry.admin_name}</span>
                        <Badge variant="outline" className="text-[8px] uppercase tracking-wider border border-white/10 text-[#A1A1AA] ml-1">
                          {entry.admin_role === "master_admin" ? "Master" : "Slave"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-[#A1A1AA] max-w-[300px] truncate">{entry.details}</TableCell>
                    <TableCell className="text-xs text-[#A1A1AA] whitespace-nowrap">{timeAgo(entry.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
